import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { products } from '../data/products';
import { isExcelAfterDark, siteConfig } from '../config/site';

const SITE_URL = siteConfig.url;
const SITE_NAME = siteConfig.name;
const DEFAULT_DESCRIPTION = siteConfig.description;

type PageSeo = {
  title: string;
  description: string;
  noIndex?: boolean;
};

const pages: Record<string, PageSeo> = {
  '/': {
    title: isExcelAfterDark
      ? 'Excel After Dark | Original Excel Tools Built After Hours'
      : 'How Do You Excel? | Excel Add-ins, Tools and Tutorials',
    description: DEFAULT_DESCRIPTION,
  },
  '/products': {
    title: `Excel Products and Add-ins | ${SITE_NAME}`,
    description: 'Explore practical Excel add-ins, templates, and tools designed to make spreadsheet work faster and easier.',
  },
  '/tutorials': {
    title: `Excel Tutorials and Methods | ${SITE_NAME}`,
    description: 'Learn useful Excel methods, formulas, automation techniques, and practical spreadsheet workflows.',
  },
  '/community': {
    title: `Excel Community | ${SITE_NAME}`,
    description: 'Join discussions about Excel tools, formulas, methods, and better spreadsheet workflows.',
  },
  '/signin': {
    title: `Sign In | ${SITE_NAME}`,
    description: `Sign in to ${SITE_NAME}.`,
    noIndex: true,
  },
  '/success': {
    title: `Purchase Complete | ${SITE_NAME}`,
    description: 'Your purchase was completed successfully.',
    noIndex: true,
  },
  '/download': {
    title: `Secure Download | ${SITE_NAME}`,
    description: 'Secure product download page.',
    noIndex: true,
  },
};

function upsertMeta(name: string, content: string, property = false) {
  const attribute = property ? 'property' : 'name';
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }
  element.content = content;
}

function upsertCanonical(url: string) {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.rel = 'canonical';
    document.head.appendChild(element);
  }
  element.href = url;
}

function setStructuredData(data: object | null) {
  const id = 'page-structured-data';
  document.getElementById(id)?.remove();
  if (!data) return;
  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
}

export function Seo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const productSlug = pathname.startsWith('/products/') ? pathname.slice('/products/'.length) : null;
    const product = productSlug ? products[productSlug] : undefined;
    const isPublishedProduct = product?.slug === 'excel-colour-picker';
    const canonicalPath = product ? `/products/${product.slug}` : pathname;
    const canonicalUrl = `${SITE_URL}${canonicalPath === '/' ? '' : canonicalPath}`;
    const page = product
      ? {
          title: `${product.title} | Excel Add-in | ${SITE_NAME}`,
          description: product.description,
          noIndex: !isPublishedProduct,
        }
      : pages[pathname] || {
          title: `Page Not Found | ${SITE_NAME}`,
          description: DEFAULT_DESCRIPTION,
          noIndex: true,
        };

    document.title = page.title;
    upsertMeta('description', page.description);
    upsertMeta('robots', page.noIndex ? 'noindex, nofollow' : 'index, follow');
    upsertMeta('og:title', page.title, true);
    upsertMeta('og:description', page.description, true);
    upsertMeta('og:type', isPublishedProduct ? 'product' : 'website', true);
    upsertMeta('og:url', canonicalUrl, true);
    upsertMeta('og:site_name', SITE_NAME, true);
    upsertMeta('twitter:card', 'summary_large_image');
    upsertCanonical(canonicalUrl);

    if (product && isPublishedProduct) {
      upsertMeta('og:image', product.image, true);
      const numericPrice = product.price.replace(/[^0-9.]/g, '');
      setStructuredData({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.title,
        description: product.description,
        image: [product.image],
        sku: product.slug,
        brand: { '@type': 'Brand', name: SITE_NAME },
        offers: {
          '@type': 'Offer',
          url: canonicalUrl,
          priceCurrency: 'USD',
          price: numericPrice,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
        },
      });
    } else if (pathname === '/') {
      setStructuredData({
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Organization',
            '@id': `${SITE_URL}/#organization`,
            name: SITE_NAME,
            url: SITE_URL,
          },
          {
            '@type': 'WebSite',
            '@id': `${SITE_URL}/#website`,
            name: SITE_NAME,
            url: SITE_URL,
            publisher: { '@id': `${SITE_URL}/#organization` },
          },
        ],
      });
    } else {
      setStructuredData(null);
    }
  }, [pathname]);

  return null;
}
